/* eslint-disable jsx-a11y/anchor-is-valid */
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { menuBookEditorActions, selectMenuBookEditorFormSearchValue } from 'features/MenuBook/menuBookEditorSlice';
import { ChildSubMenu } from 'features/MenuBook/models';
import MenuDetailModal from 'features/MenuDetail/pages/MenuDetail';
import { memo, useEffect, useRef, useState } from 'react';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { ModalProps } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { MENU_TYPE, MINUS_TEXT, PRIMARY_BUTTON } from 'utils';
import SubTitleDetailModal from '../../../SubTitleMenu/pages/detail/Detail';
import { wasMultiSelectKeyUsed, wasToggleInSelectionGroupKeyUsed } from '../../utils';
import { COLUMN_WIDTH, getItemStyle } from '../MenuBookEditorModal';

export interface ChildSubMenuRowProps {
  genreId: number;
  subgenreId: number;
  menuId: number;
  subMenuGroupId: number;
  subMenuId: number;
  childSubMenuGroupId: number;
  childSubMenuId: number;
  children?: React.ReactNode;
  index: number;
}

function ChildSubMenuRow({
  genreId,
  subgenreId,
  menuId,
  subMenuGroupId,
  subMenuId,
  childSubMenuGroupId,
  childSubMenuId,
  children,
  index,
}: ChildSubMenuRowProps) {
  const dispatch = useAppDispatch();

  const childSubMenu = useAppSelector((state) => {
    return state.menuBookEditor.data
      .flatMap((genre) => (genre.id === genreId ? genre.subgenres : []))
      .flatMap((subgenre) => (subgenre.id === subgenreId ? subgenre.menus : []))
      .flatMap((menu) => (menu.id === menuId ? menu.sub_menu_groups : []))
      .flatMap((subMenuGroup) => (subMenuGroup.id === subMenuGroupId ? subMenuGroup.sub_menus : []))
      .flatMap((subMenu) => (subMenu.id === subMenuId ? subMenu.child_sub_menu_groups : []))
      .flatMap((childSubMenuGroup) =>
        childSubMenuGroup.id === childSubMenuGroupId ? childSubMenuGroup.child_sub_menus : []
      )
      .find((childSubMenu) => childSubMenu.id === childSubMenuId);
  }) as ChildSubMenu;

  // const isExpand = useAppSelector(selectMenuBookEditorExpandAll);
  const formSearchValue = useAppSelector(selectMenuBookEditorFormSearchValue);

  // states
  // const [open, setOpen] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);

  // ref
  const menuDetailModalRef = useRef<ModalProps>(MenuDetailModal);
  // const subMenuDetailModalRef = useRef<ModalProps>(SubMenuDetailModal);
  // const isLoadedChildRef = useRef(false);

  // Subtitle Menu Modal
  const [showSubTitleMenuDetailModal, setShowSubTitleMenuDetailModal] = useState(false);
  const [subTitleMenuDetailModalData, setSubTitleMenuDetailModalData] = useState<{
    subMenuGroupId: number;
    id: number;
    targetDate: Date;
  }>();

  const pathId = `${genreId}-${subgenreId}-${menuId}-${subMenuGroupId}-${subMenuId}-${childSubMenuGroupId}-${childSubMenu.id}`;
  const droppableId = `child_sub_menu_group-${genreId}-${subgenreId}-${menuId}-${subMenuGroupId}-${subMenuId}-${childSubMenuGroupId}`;
  const isSelected = false;
  const isGhosting = false;
  const mbEditorContentElement = document.getElementById('mb-editor-table');

  useEffect(() => {
    if (showMenuModal) {
      handleOpenPopupSelectedSubMenuId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMenuModal]);

  // useEffect(() => {
  //   const btnExpandElem = document.querySelector(`#childsubmenu-${pathId}-row .dan-btn-expand`) as HTMLButtonElement;
  //   function handleBtnExpandElemClick(event: MouseEvent) {
  //     event.stopPropagation();
  //     setOpen(!open);
  //     isLoadedChildRef.current = true;
  //     btnExpandElem.innerHTML = !open ? MINUS_TEXT : PLUS_TEXT;
  //     btnExpandElem?.setAttribute('aria-expanded', !open ? 'true' : 'false');
  //   }

  //   if (btnExpandElem) {
  //     btnExpandElem.addEventListener('click', handleBtnExpandElemClick);
  //   }

  //   return () => {
  //     if (btnExpandElem) {
  //       btnExpandElem.removeEventListener('click', handleBtnExpandElemClick);
  //     }
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [open]);

  // useEffect(() => {
  //   if (isExpand !== undefined) {
  //     setOpen(isExpand && isLoadedChildRef.current);
  //   }
  // }, [isExpand]);

  function onKeyDown(
    event: React.KeyboardEvent<HTMLDivElement>,
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot
  ) {
    if (event.defaultPrevented) {
      return;
    }

    if (snapshot.isDragging) {
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    // we are using the event for selection
    event.preventDefault();

    performAction(event);
  }

  // Using onClick as it will be correctly
  // preventing if there was a drag
  function onClick(event: React.MouseEvent<HTMLDivElement>) {
    const targetElement = event.target as HTMLElement;
    const isInsideParent = mbEditorContentElement && mbEditorContentElement.contains(targetElement);
    if (!isInsideParent) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    if (event.button !== PRIMARY_BUTTON) {
      return;
    }

    // marking the event as used
    // const targetElement = event.target as HTMLElement;
    if (
      !targetElement.classList.contains('form-check-input') &&
      !targetElement.classList.contains('dan-btn-expand') &&
      !Array.from(targetElement.classList).some((className) => className.includes('btn'))
    ) {
      event.preventDefault();
    } else {
      return;
    }

    performAction(event);
  }

  function onTouchEnd(event: React.TouchEvent<HTMLDivElement>) {
    const targetElement = event.target as HTMLElement;
    const isInsideParent = mbEditorContentElement && mbEditorContentElement.contains(targetElement);
    if (!isInsideParent) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    // marking the event as used
    // we would also need to add some extra logic to prevent the click
    // if this element was an anchor
    if (
      !targetElement.classList.contains('form-check-input') &&
      !targetElement.classList.contains('dan-btn-expand') &&
      !Array.from(targetElement.classList).some((className) => className.includes('btn'))
    ) {
      event.preventDefault();
    }

    dispatch(
      menuBookEditorActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: childSubMenu.id })
    );
  }

  function performAction(event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) {
    if (wasToggleInSelectionGroupKeyUsed(event)) {
      dispatch(
        menuBookEditorActions.toggleSelectionInGroup({ selectedDroppableId: droppableId, itemId: childSubMenu.id })
      );
      return;
    }

    if (wasMultiSelectKeyUsed(event)) {
      dispatch(
        menuBookEditorActions.multiSelectTo({
          arg: {
            // data,
            // selectedItemIds,
            type: 'child_sub_menu',
            droppableId: droppableId,
          },
          newItemId: childSubMenu.id,
        })
      );
      return;
    }
    dispatch(menuBookEditorActions.toggleSelection({ selectedDroppableId: droppableId, itemId: childSubMenu.id }));
  }

  function handleOpenSubMenuDetailModal() {
    setShowMenuModal(true);
  }
  // open subtitle menu modal events
  function handleOpenSubTitleMenuDetailModal() {
    setShowSubTitleMenuDetailModal(true);
    const targetDate = formSearchValue?.dataDate || new Date();
    setSubTitleMenuDetailModalData({ subMenuGroupId: subMenuGroupId, id: childSubMenu.id, targetDate: targetDate });
  }

  function handleCloseSubTitleMenuDetailModal(status: boolean) {
    if (status) {
      //reload sub_menu list in sub_menu_group
      const targetDate = formSearchValue?.dataDate || new Date();
      dispatch(menuBookEditorActions.fetchSubMenuGroupData({ subMenuGroupId: subMenuGroupId, dataDate: targetDate }));
    }

    setShowSubTitleMenuDetailModal(false);
    setSubTitleMenuDetailModalData(undefined);
  }

  // open submenu
  function handleOpenPopupSelectedSubMenuId() {
    if (formSearchValue && formSearchValue.dataDate) {
      const strDataDate =
        formSearchValue.dataDate.getFullYear() +
        '' +
        (formSearchValue.dataDate.getMonth() + 1) +
        '' +
        formSearchValue.dataDate.getDate();
      menuDetailModalRef.current.openModalExternal(childSubMenuId, strDataDate);
    }
  }

  function handleMenuDetailModalClose() {
    menuDetailModalRef.current.onReset();
    setShowMenuModal(false);
  }

  return (
    <Draggable draggableId={`child_sub_menu-${pathId}`} index={index}>
      {(provided, snapshot) => {
        return (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style, isSelected, isGhosting)}
            onClick={onClick}
            onTouchEnd={onTouchEnd}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => onKeyDown(event, provided, snapshot)}
          >
            <div id={`${`childsubmenu-${pathId}`}-row`} className="dan-tr">
              <div
                className="dan-td"
                style={{
                  width: `${COLUMN_WIDTH.slice(0, 8).reduce((sum, width) => sum + width, 0)}%`,
                }}
              ></div>
              <div className="dan-td" style={{ width: `${COLUMN_WIDTH[8]}%` }}>
                {/* <button
                  className="dan-btn-expand"
                  tabIndex={-1}
                  aria-controls={`childsubmenu-${pathId}-content`}
                  aria-expanded={false}
                >
                  {open && isLoadedChildRef.current ? MINUS_TEXT : PLUS_TEXT}
                </button> */}
                <button
                  className="dan-btn-expand"
                  tabIndex={-1}
                  aria-controls={`childsubmenu-${pathId}-content`}
                  aria-expanded={false}
                >
                  {MINUS_TEXT}
                </button>
                <a
                  data-childsubmenugroupid={childSubMenuGroupId}
                  data-childsubmenuid={childSubMenuId}
                  style={{ minWidth: 'inherit' }}
                  className="px-0 btn btn-link"
                  onClick={
                    childSubMenu.type === MENU_TYPE.SUB_TITLE_MENU
                      ? handleOpenSubTitleMenuDetailModal
                      : handleOpenSubMenuDetailModal
                  }
                >
                  {childSubMenu.name}
                </a>
              </div>
              <div className="dan-td text-center justify-content-center" style={{ width: `${COLUMN_WIDTH[9]}%` }}>
                {childSubMenu.priority}
              </div>
            </div>
            {/* <div style={{ display: open && isLoadedChildRef.current ? 'block' : 'none' }}>
              {isLoadedChildRef.current ? (
                <div id={`childsubmenu-${pathId}-content`}>{children}</div>
              ) : (
                open && (
                  <Suspense
                    fallback={
                      <div className="dan-tr">
                        <div className="dan-td" style={{ width: '100%' }}>
                          Loading...
                        </div>
                      </div>
                    }
                  >
                    <div id={`childsubmenu-${pathId}-content`}>{children}</div>
                  </Suspense>
                )
              )}
            </div> */}

            {showMenuModal &&
              createPortal(
                <MenuDetailModal
                  ref={menuDetailModalRef}
                  onMenuModalClose={handleMenuDetailModalClose}
                  // menuCallBack={handleMenuDetailModalSaveSuccess}
                />,
                document.body
              )}

            {showSubTitleMenuDetailModal &&
              subTitleMenuDetailModalData &&
              createPortal(
                <SubTitleDetailModal
                  id={subTitleMenuDetailModalData?.id.toString() || ''}
                  show={true}
                  targetDate={subTitleMenuDetailModalData.targetDate}
                  onClose={handleCloseSubTitleMenuDetailModal}
                />,
                document.body
              )}
          </div>
        );
      }}
    </Draggable>
  );
}

export default memo(ChildSubMenuRow);
